import { useAuth as useClerkAuth } from "@clerk/react";

// E2E auth bypass. When the client bundle is built/served with
// VITE_E2E_AUTH_BYPASS === "1" (set ONLY by the onboarding e2e harness in
// script/e2e-onboarding.ts — never in dev or prod), the Clerk auth gate is
// short-circuited: the app behaves as a signed-in user that carries NO Clerk
// session token. API calls then fall through to the server's test-only
// OWNER_USER_ID fallback (armed by NODE_ENV=test). This is the client half of
// the same test gate that arms the server fallback; it can never fire in a
// normal build because the env var is unset (replaced with "" by Vite's
// `define`).
//
// Clerk's hosted sign-in rejects the e2e preview origin (it isn't a registered
// Clerk instance), so a real click-through login is impossible in the isolated
// environment — this bypass is the supported way to cover the new-user
// signup→onboarding→dashboard path end-to-end.
export const E2E_AUTH_BYPASS =
  import.meta.env.DEV &&
  import.meta.env.MODE === "development" &&
  import.meta.env.VITE_E2E_AUTH_BYPASS === "1";

type AppAuth = {
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  userId: string | null;
  getToken: () => Promise<string | null>;
};

// Drop-in replacement for Clerk's `useAuth()` at the app's auth-gate call
// sites. In normal builds it forwards Clerk's real auth state verbatim; under
// the e2e bypass it reports a signed-in, token-less session. `useClerkAuth()`
// is always called (keeping hook order stable) but its result is ignored when
// the bypass is active.
export function useAppAuth(): AppAuth {
  const clerk = useClerkAuth();
  if (E2E_AUTH_BYPASS) {
    return {
      isLoaded: true,
      isSignedIn: true,
      userId: "e2e-auth-bypass",
      getToken: async () => null,
    };
  }
  return {
    isLoaded: clerk.isLoaded,
    isSignedIn: clerk.isSignedIn,
    userId: clerk.userId ?? null,
    getToken: () => clerk.getToken(),
  };
}
